// client/src/pages/secure/HRCompliancePage.js — employment-law compliance for W-2 staff.
//
// Five tabs: To-do (every open step, soonest first), Employees (profile, cases, paid
// leave), Firm obligations (posters, policies, programs), Law library (every rule with
// its citation and whether it applies here), Settings (the facts the rules turn on).
// Admin edits happen in pop-up modals, as elsewhere in the portal.
//
// The rules and citations come from the server (hr_requirements); nothing about the law
// is hard-coded here. The managing broker corrects a rule on the Law library tab
// (components/secure/HrLawLibraryPanel.js); the seed no longer overwrites those edits.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import HrLawLibraryPanel from '../../components/secure/HrLawLibraryPanel';
import DataList from '../../components/shared/DataList';

const TABS = [['todo', 'To-do'], ['employees', 'Employees'], ['firm', 'Firm obligations'],
              ['library', 'Law library'], ['settings', 'Settings']];
const CASE_LABEL = { hire: 'Hire', separation: 'Separation', leave: 'Leave', discipline: 'Discipline',
                     records_request: 'Records request', annual: 'Annual cycle' };
const JUR = { federal: 'Federal', illinois: 'Illinois', cook_county: 'Cook County', chicago: 'Chicago', florida: 'Florida', firm: 'Firm' };
const JUR_STYLE = { federal: 'bg-blue-100 text-blue-800', illinois: 'bg-emerald-100 text-emerald-800',
    cook_county: 'bg-amber-100 text-amber-800', chicago: 'bg-purple-100 text-purple-800', florida: 'bg-orange-100 text-orange-800', firm: 'bg-slate-200 text-slate-700' };
const LOCATIONS = [['chicago', 'City of Chicago'], ['cook_suburban', 'Suburban Cook County'],
                   ['illinois_other', 'Illinois, outside Cook County'], ['florida', 'Florida']];

const Badge = ({ j }) => <span className={`px-2 py-0.5 rounded text-xs font-medium ${JUR_STYLE[j] || JUR_STYLE.firm}`}>{JUR[j] || j}</span>;
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString() : '—');

const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start p-4 overflow-y-auto">
        <div className={`bg-white p-6 md:p-8 rounded-lg shadow-xl w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} my-8`}>
            <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-slate-800">{title}</h2>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none" aria-label="Close">&times;</button>
            </div>
            {children}
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// A case: its checklist, with issue-form, done and not-applicable controls
// ---------------------------------------------------------------------------
const CaseModal = ({ caseId, onClose, onChanged }) => {
    const api = useApiClient();
    const [c, setC] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [naItem, setNaItem] = useState(null);
    const [naNote, setNaNote] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try { setC(await api.get(`/api/hr/cases/${caseId}`)); setError(''); }
        catch (e) { setError(e.message); }
    }, [api, caseId]);
    useEffect(() => { load(); }, [load]);

    const patch = async (item, body) => {
        setBusy(item.item_id); setError('');
        try { setC(await api.patch(`/api/hr/cases/${caseId}/items/${item.item_id}`, body)); onChanged && onChanged(); }
        catch (e) { setError(e.message); }
        finally { setBusy(null); }
    };

    const issue = async (item) => {
        setBusy(item.item_id); setError(''); setNotice('');
        try {
            const fields = await api.get(`/api/hr/cases/${caseId}/form-fields`);
            const r = await api.post('/api/forms-engine/generate', {
                form_key: item.form_key, subject_type: 'employee', subject_id: fields.subject_id, extra: fields.extra,
            });
            const made = r?.created?.[0];
            if (!made) throw new Error(r?.skipped?.[0]?.reason || 'The form was not created.');
            await api.patch(`/api/hr/cases/${caseId}/items/${item.item_id}`, { form_instance_id: made.instance_id });
            const blanks = (made.blanks || []).length;
            setNotice(`${made.title} issued${blanks ? ` — ${blanks} field(s) left blank to fill by hand` : ''}. `
                + (made.needs_upload ? 'Upload the completed official form' : 'Send it for signature or record delivery')
                + ' from the employee\'s Forms panel. This step closes itself when it is signed or delivered.');
            load();
        } catch (e) { setError(e.message); }
        finally { setBusy(null); }
    };

    const setFact = async (key, value) => {
        try {
            const r = await api.patch(`/api/hr/cases/${caseId}/facts`, { facts: { [key]: value } });
            setC(r.case); setNotice(r.message);
        } catch (e) { setError(e.message); }
    };

    const close = async () => {
        setError('');
        try { const r = await api.post(`/api/hr/cases/${caseId}/close`, {}); setNotice(r.message); load(); onChanged && onChanged(); }
        catch (e) { setError(`${e.message}${e.payload?.open ? ` — ${e.payload.open.join('; ')}` : ''}`); }
    };

    if (!c) return <Modal title="Case" onClose={onClose}>{error ? <p className="text-red-600">{error}</p> : 'Loading…'}</Modal>;

    const open = c.items.filter((i) => i.status === 'open');
    return (
        <Modal wide onClose={onClose}
               title={`${CASE_LABEL[c.case_type]} — ${c.first_name} ${c.last_name}${c.cycle_year ? ` · ${c.cycle_year}` : ''}`}>
            <p className="text-sm text-slate-600 mb-3">
                {c.case_type === 'hire' ? 'Start date' : c.case_type === 'separation' ? 'Last day' : c.case_type === 'records_request' ? 'Request received' : 'Date'}: <strong>{fmtDate(c.event_date)}</strong>
                {c.separation_type ? ` · ${c.separation_type.replace('_', ' ')}` : ''}
                {c.leave_type ? ` · ${c.leave_type}` : ''} · {c.status === 'open' ? `${open.length} open` : 'closed'}
                {c.i9_retain_until ? ` · I-9 may be destroyed after ${fmtDate(c.i9_retain_until)}` : ''}
            </p>

            {c.case_type === 'separation' && c.status === 'open' && (
                <div className="flex flex-wrap gap-4 text-sm bg-slate-50 p-3 rounded mb-3">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!c.facts.separation_agreement} onChange={(e) => setFact('separation_agreement', e.target.checked)} />
                        A separation agreement is being offered
                    </label>
                    <label className="flex items-center gap-2">Final pay date
                        <input type="date" value={c.facts.final_pay_date || ''} onChange={(e) => setFact('final_pay_date', e.target.value)} className="border rounded p-1" />
                    </label>
                </div>
            )}
            {c.case_type === 'hire' && c.status === 'open' && (
                <div className="flex flex-wrap gap-4 text-sm bg-slate-50 p-3 rounded mb-3">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={c.facts.job_posting !== false} onChange={(e) => setFact('job_posting', e.target.checked)} />
                        The role was advertised
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!c.facts.background_check} onChange={(e) => setFact('background_check', e.target.checked)} />
                        A background check will be run
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!c.facts.restrictive_covenant} onChange={(e) => setFact('restrictive_covenant', e.target.checked)} />
                        Non-compete / non-solicit
                    </label>
                </div>
            )}

            {notice && <div className="p-3 mb-3 rounded bg-green-50 text-green-800 text-sm">{notice}</div>}
            {error && <div className="p-3 mb-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

            <ul className="divide-y border rounded">
                {c.items.map((i) => (
                    <li key={i.item_id} className={`p-3 ${i.status !== 'open' ? 'bg-slate-50' : ''}`}>
                        <div className="flex flex-wrap justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge j={i.jurisdiction} />
                                    {i.is_blocking ? <span className="text-xs font-semibold text-red-700">Required</span> : null}
                                    <span className={`font-medium ${i.status !== 'open' ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{i.title}</span>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{i.action}</p>
                                <p className="text-xs text-slate-500 mt-1">{i.authority}</p>
                                {i.note && <p className="text-xs text-slate-700 mt-1 italic">Note: {i.note}</p>}
                            </div>
                            <div className="text-right text-sm whitespace-nowrap">
                                <div className={i.overdue ? 'text-red-700 font-semibold' : 'text-slate-600'}>
                                    {i.status === 'done' ? `Done ${fmtDate(i.completed_on)}` : i.status === 'not_applicable' ? 'Not applicable' : (i.due_date ? `Due ${fmtDate(i.due_date)}` : 'No fixed date')}
                                </div>
                                {i.form_instance_id && <div className="text-xs text-slate-500">Form: {i.form_status}{i.form_delivered_at ? ' · delivered' : ''}</div>}
                                {i.status === 'open' && c.status === 'open' && (
                                    <div className="flex gap-2 justify-end mt-2">
                                        {i.form_key && !i.form_instance_id &&
                                            <button disabled={busy === i.item_id} onClick={() => issue(i)} className="px-2 py-1 text-xs bg-slate-800 text-white rounded disabled:bg-indigo-300">Issue form</button>}
                                        {(!i.form_key || i.form_instance_id) &&
                                            <button disabled={busy === i.item_id} onClick={() => patch(i, { status: 'done' })} className="px-2 py-1 text-xs bg-emerald-600 text-white rounded">Done</button>}
                                        {i.form_key && !i.form_instance_id &&
                                            <button disabled={busy === i.item_id} onClick={() => { const n = window.prompt('Where is the completed copy kept? (e.g. "wet I-9 in the I-9 binder")'); if (n) patch(i, { status: 'done', note: n }); }} className="px-2 py-1 text-xs bg-slate-200 rounded">Done on paper</button>}
                                        <button onClick={() => { setNaItem(i); setNaNote(''); }} className="px-2 py-1 text-xs bg-slate-200 rounded">N/A</button>
                                    </div>
                                )}
                                {i.status !== 'open' && c.status === 'open' &&
                                    <button onClick={() => patch(i, { status: 'open' })} className="text-xs text-indigo-600 hover:underline mt-1">Reopen</button>}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            {c.not_required?.length > 0 && (
                <details className="mt-4 text-sm">
                    <summary className="cursor-pointer text-slate-600">Why these are not on the list ({c.not_required.length})</summary>
                    <ul className="mt-2 space-y-1">
                        {c.not_required.map((x) => <li key={x.req_key} className="text-slate-500">{x.title} — <em>{x.reason}</em></li>)}
                    </ul>
                </details>
            )}

            <div className="flex justify-between items-center mt-6">
                <Link to={`/dashboard/employees/${c.employee_id}`} className="text-sm text-indigo-600 hover:underline">Employee page and Forms panel &rarr;</Link>
                {c.status === 'open' && <button onClick={close} className="px-4 py-2 bg-slate-800 text-white rounded text-sm">Close case</button>}
            </div>

            {naItem && (
                <Modal title="Not applicable — why?" onClose={() => setNaItem(null)}>
                    <p className="text-sm text-slate-600 mb-2">{naItem.title}</p>
                    <textarea value={naNote} onChange={(e) => setNaNote(e.target.value)} rows={3} className="w-full border rounded p-2" placeholder="The reason, for the file" />
                    <div className="flex justify-end gap-3 mt-3">
                        <button onClick={() => setNaItem(null)} className="px-3 py-2 bg-gray-200 rounded">Cancel</button>
                        <button disabled={!naNote.trim()} onClick={() => { patch(naItem, { status: 'not_applicable', note: naNote }); setNaItem(null); }} className="px-3 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-300">Save</button>
                    </div>
                </Modal>
            )}
        </Modal>
    );
};

// ---------------------------------------------------------------------------
// An employee: profile, cases, paid leave
// ---------------------------------------------------------------------------
const EmployeeModal = ({ employeeId, onClose, onOpenCase, onChanged }) => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [profile, setProfile] = useState({});
    const [leave, setLeave] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [newCase, setNewCase] = useState(null);
    const [entry, setEntry] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await api.get(`/api/hr/employees/${employeeId}`);
            setData(d); setProfile(d.profile || {});
            setLeave(await api.get(`/api/hr/employees/${employeeId}/leave`));
        } catch (e) { setError(e.message); }
    }, [api, employeeId]);
    useEffect(() => { load(); }, [load]);

    const saveProfile = async () => {
        setError(''); setNotice('');
        try { await api.put(`/api/hr/employees/${employeeId}/profile`, profile); setNotice('Profile saved.'); load(); }
        catch (e) { setError(e.message); }
    };

    const startCase = async () => {
        setError('');
        try {
            const r = await api.post('/api/hr/cases', { employee_id: employeeId, ...newCase });
            setNewCase(null); onChanged && onChanged(); load(); onOpenCase(r.case_id);
        } catch (e) { setError(e.message); }
    };

    const addLeave = async () => {
        setError('');
        try { await api.post(`/api/hr/employees/${employeeId}/leave`, entry); setEntry(null); load(); }
        catch (e) { setError(e.message); }
    };

    if (!data) return <Modal title="Employee" onClose={onClose}>{error || 'Loading…'}</Modal>;
    const e = data.employee;
    const P = (k) => ({ value: profile[k] ?? '', onChange: (ev) => setProfile((p) => ({ ...p, [k]: ev.target.value })) });
    const C = (k) => ({ checked: !!profile[k], onChange: (ev) => setProfile((p) => ({ ...p, [k]: ev.target.checked })) });
    const buckets = leave?.rules?.buckets || [];

    return (
        <Modal wide onClose={onClose} title={`${e.first_name} ${e.last_name} — ${e.job_title || ''}`}>
            {notice && <div className="p-3 mb-3 rounded bg-green-50 text-green-800 text-sm">{notice}</div>}
            {error && <div className="p-3 mb-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

            <h3 className="font-semibold text-slate-800 mb-2">HR profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <label>Works in
                    <select {...P('work_location')} className="block w-full border rounded p-2">
                        <option value="">Same as the office</option>
                        {LOCATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        <option value="out_of_state">Another state</option>
                    </select>
                </label>
                {profile.work_location === 'out_of_state' &&
                    <label>State<input {...P('work_state')} maxLength={2} className="block w-full border rounded p-2 uppercase" /></label>}
                <label>Overtime status
                    <select {...P('flsa_status')} className="block w-full border rounded p-2">
                        <option value="unknown">Not yet decided</option><option value="non_exempt">Non-exempt</option><option value="exempt">Exempt</option>
                    </select>
                </label>
                <label>Pay basis
                    <select {...P('pay_basis')} className="block w-full border rounded p-2">
                        <option value="">—</option><option value="hourly">Hourly</option><option value="salary">Salary</option><option value="commission">Commission</option><option value="other">Other</option>
                    </select>
                </label>
                <label>Pay rate ($)<input type="number" step="0.01" {...P('pay_rate')} className="block w-full border rounded p-2" /></label>
                <label>Annualized earnings ($)<input type="number" step="0.01" {...P('annualized_earnings')} className="block w-full border rounded p-2" /></label>
                <label>Work authorization expires<input type="date" {...P('work_authorization_expires')} className="block w-full border rounded p-2" /></label>
            </div>
            <div className="flex flex-wrap gap-4 text-sm mt-3">
                <label className="flex items-center gap-2"><input type="checkbox" {...C('is_supervisor')} /> Supervises others</label>
                <label className="flex items-center gap-2"><input type="checkbox" {...C('age_40_plus')} /> Aged 40 or over</label>
                <label className="flex items-center gap-2"><input type="checkbox" {...C('temporary_work_authorization')} /> Work authorization expires</label>
                <label className="flex items-center gap-2"><input type="checkbox" {...C('enrolled_group_health')} /> On the group health plan</label>
            </div>
            <div className="text-right mt-3"><button onClick={saveProfile} className="px-4 py-2 bg-slate-800 text-white rounded text-sm">Save profile</button></div>

            <div className="flex justify-between items-center mt-6 mb-2">
                <h3 className="font-semibold text-slate-800">Cases</h3>
                <button onClick={() => setNewCase({ case_type: 'hire', event_date: new Date().toISOString().slice(0, 10) })} className="text-sm text-indigo-600 hover:underline">+ Open a case</button>
            </div>
            <ul className="divide-y border rounded text-sm">
                {data.cases.map((k) => (
                    <li key={k.case_id} className="flex justify-between items-center p-3">
                        <span><strong>{CASE_LABEL[k.case_type]}</strong> · {fmtDate(k.event_date)}{k.separation_type ? ` · ${k.separation_type}` : ''} · {k.status}{k.status === 'open' ? ` · ${k.open_items} open` : ''}</span>
                        <button onClick={() => onOpenCase(k.case_id)} className="text-indigo-600 hover:underline">Open</button>
                    </li>
                ))}
                {!data.cases.length && <li className="p-3 text-slate-500">No cases yet. Start with a Hire case, even for someone already employed, to check their file is complete.</li>}
            </ul>

            <div className="flex justify-between items-center mt-6 mb-2">
                <h3 className="font-semibold text-slate-800">Paid leave</h3>
                <button onClick={() => setEntry({ leave_bucket: buckets[0]?.bucket || 'paid_leave', entry_kind: 'usage', entry_date: new Date().toISOString().slice(0, 10), hours: '', memo: '' })} className="text-sm text-indigo-600 hover:underline">+ Record</button>
            </div>
            <p className="text-xs text-slate-500 mb-2">{leave?.rules?.law}</p>
            <div className="flex flex-wrap gap-3 mb-2">
                {buckets.map((b) => (
                    <div key={b.bucket} className="border rounded p-3 text-sm">
                        <div className="font-semibold">{b.bucket === 'paid_sick_leave' ? 'Paid sick leave' : 'Paid leave'}: {leave?.balances?.[b.bucket] ?? 0} h</div>
                        <div className="text-xs text-slate-500">1 h per {b.per_hours} worked, up to {b.cap} h a year; usable after day {b.usable_after_days}{b.carryover != null ? `; carry over up to ${b.carryover} h` : ''}</div>
                    </div>
                ))}
            </div>
            <ul className="text-xs text-slate-600 max-h-40 overflow-y-auto">
                {(leave?.entries || []).map((x) => <li key={x.entry_id}>{fmtDate(x.entry_date)} · {x.leave_bucket.replace(/_/g, ' ')} · {x.entry_kind} · {x.hours} h{x.memo ? ` · ${x.memo}` : ''}</li>)}
            </ul>

            {newCase && (
                <Modal title="Open a case" onClose={() => setNewCase(null)}>
                    <div className="space-y-3 text-sm">
                        <select value={newCase.case_type} onChange={(ev) => setNewCase((n) => ({ ...n, case_type: ev.target.value }))} className="w-full border rounded p-2">
                            {Object.entries(CASE_LABEL).filter(([k]) => k !== 'annual').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <label className="block">{newCase.case_type === 'hire' ? 'Start date' : newCase.case_type === 'separation' ? 'Last day of employment' : newCase.case_type === 'records_request' ? 'Date the request was received' : 'Date'}
                            <input type="date" value={newCase.event_date} onChange={(ev) => setNewCase((n) => ({ ...n, event_date: ev.target.value }))} className="block w-full border rounded p-2" />
                        </label>
                        {newCase.case_type === 'separation' && (
                            <select value={newCase.separation_type || ''} onChange={(ev) => setNewCase((n) => ({ ...n, separation_type: ev.target.value }))} className="w-full border rounded p-2">
                                <option value="">Type of separation…</option>
                                <option value="resignation">Resignation</option><option value="involuntary">Involuntary (termination)</option>
                                <option value="layoff">Layoff</option><option value="retirement">Retirement</option>
                                <option value="end_of_term">End of fixed term</option><option value="death">Death</option>
                            </select>
                        )}
                        {newCase.case_type === 'leave' &&
                            <input placeholder="Kind of leave (e.g. medical, family, military, jury)" value={newCase.leave_type || ''} onChange={(ev) => setNewCase((n) => ({ ...n, leave_type: ev.target.value }))} className="w-full border rounded p-2" />}
                        <textarea placeholder="Notes (facts only)" value={newCase.notes || ''} onChange={(ev) => setNewCase((n) => ({ ...n, notes: ev.target.value }))} className="w-full border rounded p-2" rows={2} />
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setNewCase(null)} className="px-3 py-2 bg-gray-200 rounded">Cancel</button>
                            <button onClick={startCase} disabled={newCase.case_type === 'separation' && !newCase.separation_type} className="px-3 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-300">Open case</button>
                        </div>
                    </div>
                </Modal>
            )}
            {entry && (
                <Modal title="Record paid leave" onClose={() => setEntry(null)}>
                    <div className="space-y-3 text-sm">
                        <select value={entry.leave_bucket} onChange={(ev) => setEntry((x) => ({ ...x, leave_bucket: ev.target.value }))} className="w-full border rounded p-2">
                            <option value="paid_leave">Paid leave</option>
                            {buckets.some((b) => b.bucket === 'paid_sick_leave') && <option value="paid_sick_leave">Paid sick leave</option>}
                        </select>
                        <select value={entry.entry_kind} onChange={(ev) => setEntry((x) => ({ ...x, entry_kind: ev.target.value }))} className="w-full border rounded p-2">
                            <option value="usage">Used</option><option value="accrual">Earned (accrual)</option><option value="frontload">Front-loaded for the year</option>
                            <option value="carryover">Carried over</option><option value="payout">Paid out</option><option value="forfeit">Forfeited</option><option value="adjustment">Adjustment (+/−, needs a reason)</option>
                        </select>
                        <input type="date" value={entry.entry_date} onChange={(ev) => setEntry((x) => ({ ...x, entry_date: ev.target.value }))} className="w-full border rounded p-2" />
                        <input type="number" step="0.25" placeholder="Hours" value={entry.hours} onChange={(ev) => setEntry((x) => ({ ...x, hours: ev.target.value }))} className="w-full border rounded p-2" />
                        <input placeholder="Memo" value={entry.memo} onChange={(ev) => setEntry((x) => ({ ...x, memo: ev.target.value }))} className="w-full border rounded p-2" />
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setEntry(null)} className="px-3 py-2 bg-gray-200 rounded">Cancel</button>
                            <button onClick={addLeave} className="px-3 py-2 bg-slate-800 text-white rounded">Record</button>
                        </div>
                    </div>
                </Modal>
            )}
        </Modal>
    );
};

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------
const HRCompliancePage = () => {
    const api = useApiClient();
    const [tab, setTab] = useState('todo');
    const [overview, setOverview] = useState(null);
    const [todo, setTodo] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [firm, setFirm] = useState([]);
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [openCase, setOpenCase] = useState(null);
    const [openEmployee, setOpenEmployee] = useState(null);
    const [firmEdit, setFirmEdit] = useState(null);
    const [showAll, setShowAll] = useState(false);

    // Send a new install to Settings once, on the first load only. load() also runs after
    // every change on the Law library tab; jumping tabs then unmounted the library (and the
    // rule being edited) mid-task whenever the office location was not set yet.
    const sentToSettings = useRef(false);
    const load = useCallback(async () => {
        try {
            const [o, t, e, f, s] = await Promise.all([
                api.get('/api/hr/overview'), api.get('/api/hr/dashboard'), api.get('/api/hr/employees'),
                api.get('/api/hr/employer'), api.get('/api/hr/settings'),
            ]);
            setOverview(o); setTodo(t); setEmployees(e); setFirm(f); setSettings(s);
            if (!o.settings.office_location && !sentToSettings.current) setTab('settings');
            sentToSettings.current = true;
            setError('');
        } catch (err) { setError(err.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const saveSettings = async () => {
        setError(''); setNotice('');
        try { setSettings(await api.put('/api/hr/settings', settings)); setNotice('Settings saved — the checklists now use them.'); load(); }
        catch (err) { setError(err.message); }
    };

    const saveFirm = async () => {
        setError('');
        try {
            await api.put(`/api/hr/employer/${firmEdit.req_key}`, firmEdit);
            setFirmEdit(null); load();
        } catch (err) { setError(err.message); }
    };

    const annual = async () => {
        const year = window.prompt('Open the annual cycle for which year?', String(new Date().getFullYear()));
        if (!year) return;
        try { const r = await api.post('/api/hr/annual-cycle', { year: Number(year) }); setNotice(r.message); load(); }
        catch (err) { setError(err.message); }
    };

    const S = (k) => ({ checked: !!settings?.[k], onChange: (ev) => setSettings((s) => ({ ...s, [k]: ev.target.checked })) });
    const firmShown = firm.filter((f) => showAll || f.applies_here);

    return (
        <div className="p-4 md:p-8 space-y-6">
            <div className="flex flex-wrap justify-between items-end gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800">HR Compliance</h1>
                    <p className="text-slate-600 text-sm">Federal, state and local employment law for the firm's W-2 staff, by where each person works. Licensees under independent contractor agreements are not counted here.</p>
                </div>
                {overview && (
                    <div className="flex gap-3 text-sm">
                        <div className="bg-white shadow rounded px-3 py-2"><div className="text-xs text-slate-500">Employees</div><div className="font-bold">{overview.headcount}</div></div>
                        <div className="bg-white shadow rounded px-3 py-2"><div className="text-xs text-slate-500">Open steps</div><div className="font-bold">{overview.open_items}</div></div>
                        <div className={`shadow rounded px-3 py-2 ${overview.overdue_items ? 'bg-red-50' : 'bg-white'}`}><div className="text-xs text-slate-500">Overdue</div><div className="font-bold">{overview.overdue_items}</div></div>
                        <div className="bg-white shadow rounded px-3 py-2"><div className="text-xs text-slate-500">Firm obligations open</div><div className="font-bold">{overview.firm_open} / {overview.firm_obligations}</div></div>
                    </div>
                )}
            </div>

            {overview?.needs_setup && <div className="p-3 rounded bg-amber-50 text-amber-900 text-sm">Set the office location in <strong>Settings</strong> first. Whether Chicago, Cook County or the Illinois Paid Leave Act applies depends on it.</div>}
            {notice && <div className="p-3 rounded bg-green-50 text-green-800 text-sm">{notice}</div>}
            {error && <div className="p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

            <div className="border-b border-gray-200 flex flex-wrap gap-6">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} className={`py-3 border-b-2 text-sm font-medium ${tab === k ? 'border-slate-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
                ))}
            </div>

            {tab === 'todo' && (
                <div className="bg-white rounded-lg shadow p-4 md:p-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold">Open steps, soonest first</h2>
                        <button onClick={annual} className="text-sm px-3 py-2 bg-slate-800 text-white rounded">Open annual cycle</button>
                    </div>
                    <DataList rows={todo} rowKey="item_id" csvName="hr-open-steps" empty="Nothing open. Open a case from the Employees tab."
                        defaultSort={{ key: 'due_date', dir: 'asc' }}
                        filters={[{ key: 'jurisdiction', label: 'Jurisdiction' }, { key: 'case_type', label: 'Case' }, { key: 'state', label: 'Due' }]}
                        rowClass={(t) => (t.overdue ? 'text-red-700' : '')}
                        columns={[
                            { key: 'jurisdiction', label: 'Jurisdiction', value: (t) => JUR[t.jurisdiction] || t.jurisdiction, render: (t) => <Badge j={t.jurisdiction} /> },
                            { key: 'employee', label: 'Employee', value: (t) => `${t.first_name || ''} ${t.last_name || ''}`.trim(), render: (t) => <strong>{t.first_name} {t.last_name}</strong> },
                            { key: 'case_type', label: 'Case', value: (t) => CASE_LABEL[t.case_type] || t.case_type },
                            { key: 'title', label: 'Step', value: (t) => `${t.title}${t.is_blocking ? ' · required' : ''}`,
                                render: (t) => <>{t.title}{t.is_blocking ? <span className="text-red-700 font-semibold"> · required</span> : null}</> },
                            { key: 'due_date', label: 'Due', type: 'date',
                                render: (t) => <span className={t.overdue ? 'text-red-700 font-semibold' : 'text-slate-600'}>{t.due_date ? `${t.overdue ? 'Overdue ' : 'Due '}${fmtDate(t.due_date)}` : 'No fixed date'}</span> },
                            { key: 'state', label: 'State', value: (t) => (t.overdue ? 'Overdue' : t.due_date ? 'Due' : 'No fixed date') },
                            { key: 'open', label: '', sortable: false, csv: false, search: false, render: (t) => <button onClick={() => setOpenCase(t.case_id)} className="text-indigo-600 hover:underline">Open</button> },
                        ]} />
                </div>
            )}

            {tab === 'employees' && (
                <div className="bg-white rounded-lg shadow p-4 md:p-6 overflow-x-auto">
                    <DataList rows={employees} rowKey="employee_id" csvName="hr-employees" defaultSort={{ key: 'employee', dir: 'asc' }}
                        filters={[{ key: 'status', label: 'Status' }, { key: 'work_location', label: 'Works in' }, { key: 'flsa', label: 'Overtime' }]}
                        columns={[
                            { key: 'employee', label: 'Employee', value: (e) => `${e.first_name || ''} ${e.last_name || ''} ${e.job_title || ''}`.trim(),
                                sortValue: (e) => `${e.last_name || ''} ${e.first_name || ''}`,
                                render: (e) => <><strong>{e.first_name} {e.last_name}</strong><div className="text-xs text-slate-500">{e.job_title}</div></> },
                            { key: 'status', label: 'Status' },
                            { key: 'work_location', label: 'Works in', value: (e) => (e.work_location ? e.work_location.replace(/_/g, ' ') : 'office'),
                                render: (e) => (e.work_location ? e.work_location.replace(/_/g, ' ') : <span className="text-slate-400">office</span>) },
                            { key: 'flsa', label: 'Overtime', value: (e) => (e.flsa_status === 'unknown' || !e.flsa_status ? 'not decided' : e.flsa_status.replace('_', '-')),
                                render: (e) => (e.flsa_status === 'unknown' || !e.flsa_status ? <span className="text-amber-700">not decided</span> : e.flsa_status.replace('_', '-')) },
                            { key: 'open_cases', label: 'Open cases', type: 'number' },
                            { key: 'overdue', label: 'Overdue', type: 'number', render: (e) => <span className={e.overdue ? 'text-red-700 font-semibold' : ''}>{e.overdue}</span> },
                            { key: 'open', label: '', sortable: false, csv: false, search: false, render: (e) => <button onClick={() => setOpenEmployee(e.employee_id)} className="text-indigo-600 hover:underline">Open</button> },
                        ]} />
                </div>
            )}

            {tab === 'firm' && (
                <div className="bg-white rounded-lg shadow p-4 md:p-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold">Posters, policies, registrations and programs</h2>
                        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show ones that do not apply</label>
                    </div>
                    <DataList rows={firmShown} rowKey="req_key" csvName="hr-firm-obligations" defaultSort={{ key: 'next_due', dir: 'asc' }}
                        rowClass={(f) => (!f.applies_here ? 'opacity-60' : '')}
                        filters={[{ key: 'jurisdiction', label: 'Jurisdiction' }, { key: 'stage', label: 'Kind' }, { key: 'state', label: 'Status' }]}
                        columns={[
                            { key: 'jurisdiction', label: 'Jurisdiction', value: (f) => JUR[f.jurisdiction] || f.jurisdiction, render: (f) => <Badge j={f.jurisdiction} /> },
                            { key: 'stage', label: 'Kind', render: (f) => <span className="text-xs uppercase text-slate-500">{f.stage}</span> },
                            { key: 'title', label: 'Obligation', value: (f) => `${f.title}${f.is_blocking ? ' (required)' : ''} ${f.action || ''} ${f.authority || ''}`, sortValue: (f) => f.title,
                                render: (f) => (
                                    <div className="min-w-[16rem]">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {f.is_blocking ? <span className="text-xs font-semibold text-red-700">Required</span> : null}
                                            <span className="font-medium text-slate-800">{f.title}</span>
                                        </div>
                                        <p className="text-sm text-slate-600 mt-1">{f.action}</p>
                                        <p className="text-xs text-slate-500 mt-1">{f.authority} · <em>{f.applies_here ? `Applies: ${f.reason}` : `Does not apply: ${f.reason}`}</em></p>
                                        {f.note && <p className="text-xs italic text-slate-700 mt-1">Note: {f.note}</p>}
                                    </div>) },
                            { key: 'state', label: 'Status', value: (f) => (f.status === 'done' ? `Done ${fmtDate(f.last_done_on)}` : f.status === 'not_applicable' ? 'Not applicable' : (f.overdue ? 'Overdue' : 'Open')),
                                render: (f) => (
                                    <div className="text-right text-sm whitespace-nowrap">
                                        <div className={f.status === 'done' ? 'text-emerald-700' : f.overdue ? 'text-red-700 font-semibold' : 'text-slate-600'}>
                                            {f.status === 'done' ? `Done ${fmtDate(f.last_done_on)}` : f.status === 'not_applicable' ? 'Not applicable' : 'Open'}
                                        </div>
                                        {f.applies_here && <button onClick={() => setFirmEdit({ req_key: f.req_key, title: f.title, status: f.status === 'open' ? 'done' : f.status, note: f.note || '', last_done_on: new Date().toISOString().slice(0, 10), next_due: f.next_due || '' })} className="text-indigo-600 hover:underline text-xs mt-1">Update</button>}
                                    </div>) },
                            { key: 'next_due', label: 'Next due', type: 'date', render: (f) => (f.next_due ? fmtDate(f.next_due) : '') },
                        ]} />
                </div>
            )}

            {tab === 'library' && <HrLawLibraryPanel onChanged={load} />}

            {tab === 'settings' && settings && (
                <div className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4 text-sm max-w-3xl">
                    <label className="block font-medium">Where is the office?
                        <select value={settings.office_location || ''} onChange={(e) => setSettings((s) => ({ ...s, office_location: e.target.value || null }))} className="block w-full border rounded p-2 mt-1 font-normal">
                            <option value="">Not set</option>
                            {LOCATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </label>
                    <label className="block font-medium">Employees not in the portal who still count (e.g. out of state)
                        <input type="number" min="0" value={settings.additional_headcount ?? 0} onChange={(e) => setSettings((s) => ({ ...s, additional_headcount: e.target.value }))} className="block w-40 border rounded p-2 mt-1 font-normal" />
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('offers_group_health')} /> The firm offers a group health plan</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('offers_retirement_plan')} /> The firm offers a qualified retirement plan (401(k), SIMPLE…)</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('business_age_2y_plus')} /> The business is two or more years old</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('uses_direct_deposit')} /> Pay by direct deposit is offered</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('uses_background_checks')} /> Background checks are used</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('uses_e_verify')} /> The firm uses E-Verify</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('uses_ai_in_employment')} /> AI is used in hiring or employment decisions</label>
                        <label className="flex items-center gap-2"><input type="checkbox" {...S('uses_restrictive_covenants')} /> Non-competes or non-solicits are used</label>
                    </div>
                    <div className="text-right"><button onClick={saveSettings} className="px-4 py-2 bg-slate-800 text-white rounded">Save settings</button></div>
                </div>
            )}

            {openEmployee && <EmployeeModal employeeId={openEmployee} onClose={() => setOpenEmployee(null)} onChanged={load} onOpenCase={(id) => { setOpenCase(id); }} />}
            {openCase && <CaseModal caseId={openCase} onClose={() => { setOpenCase(null); load(); }} onChanged={load} />}
            {firmEdit && (
                <Modal title={firmEdit.title} onClose={() => setFirmEdit(null)}>
                    <div className="space-y-3 text-sm">
                        <select value={firmEdit.status} onChange={(e) => setFirmEdit((f) => ({ ...f, status: e.target.value }))} className="w-full border rounded p-2">
                            <option value="done">Done</option><option value="open">Open</option><option value="not_applicable">Not applicable</option>
                        </select>
                        {firmEdit.status === 'done' && <label className="block">Done on<input type="date" value={firmEdit.last_done_on} onChange={(e) => setFirmEdit((f) => ({ ...f, last_done_on: e.target.value }))} className="block w-full border rounded p-2" /></label>}
                        <label className="block">Next due (optional)<input type="date" value={firmEdit.next_due} onChange={(e) => setFirmEdit((f) => ({ ...f, next_due: e.target.value }))} className="block w-full border rounded p-2" /></label>
                        <textarea placeholder={firmEdit.status === 'not_applicable' ? 'Why it does not apply (required)' : 'Evidence: where posted, policy number, carrier…'} value={firmEdit.note} onChange={(e) => setFirmEdit((f) => ({ ...f, note: e.target.value }))} rows={3} className="w-full border rounded p-2" />
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setFirmEdit(null)} className="px-3 py-2 bg-gray-200 rounded">Cancel</button>
                            <button onClick={saveFirm} className="px-3 py-2 bg-slate-800 text-white rounded">Save</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default HRCompliancePage;
